import { Button } from "@/components/ui/button.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { panelHeaderControlHeightClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { cn } from "@/lib/utils.ts";
import { errorToast } from "@/components/ui/toast.tsx";
import type { MarkerSdk } from "@marker.io/browser";
import { Bug } from "lucide-react";
import { useEffect, useRef } from "react";
import { useMarkerSdk } from "./MarkerIoContext.tsx";

/**
 * Whether a press at these coordinates would land on the button.
 *
 * `e.target` cannot answer that. Radix covers the page with an invisible
 * blocker whenever a dismissable layer is open — a dropdown, a select, a modal
 * dialog — and the blocker is what the event names as its target. That is the
 * whole reason the listener below runs in the capture phase: left alone, asking
 * for a bug report from a screen with a menu up closes the menu first, and the
 * screenshot no longer shows what the user wanted to report.
 *
 * So ask the document what is actually drawn there. Radix blocks by setting
 * `pointer-events: none` on the body, which `elementFromPoint` obeys, so lift
 * it for the length of the test and put back exactly what was there. Nothing
 * paints in between — this runs start to finish inside one event handler.
 *
 * It has to be this test rather than the button's own rectangle. Up in a header
 * the button shares its corner with whatever close control an overlay puts
 * there, and a press meant for that would otherwise file a bug report on its
 * way out.
 */
const pressLandsOnButton = (button: HTMLElement, e: PointerEvent) => {
  const { style } = document.body;

  // The ordinary case, and the cheap one: with nothing blocking, the event's
  // own target is the truth and no layout has to be read. This listener sees
  // every pointerdown in the app, so the common path has to stay free.
  if (style.pointerEvents !== "none") {
    return e.target instanceof Node && button.contains(e.target);
  }

  // Blocked. Coordinates first, so a press elsewhere on the page does not pay
  // for the body write below — `pointer-events` is inherited, so writing it
  // invalidates style for the whole document.
  const rect = button.getBoundingClientRect();
  const within =
    e.clientX >= rect.left &&
    e.clientX <= rect.right &&
    e.clientY >= rect.top &&
    e.clientY <= rect.bottom;
  if (!within) return false;

  style.pointerEvents = "";
  try {
    return button.contains(document.elementFromPoint(e.clientX, e.clientY));
  } finally {
    style.pointerEvents = "none";
  }
};

/**
 * Opens the reporter, and says so when it cannot.
 *
 * `capture` returns a promise, and by the time it rejects the press that would
 * have opened the reporter has already been swallowed by the listener below —
 * so without this the button is one that visibly does nothing.
 */
const openReporter = (sdk: MarkerSdk) => {
  sdk.capture("fullscreen").catch(() => errorToast("Couldn't open the bug reporter"));
};

/**
 * Bug report button, wired to the Marker.io SDK.
 *
 * Last in the topmost panel header's control group, so reporting a problem
 * lives with the other things you can do to the page rather than floating over
 * the conversation. `HeaderUtilities` owns where it lands; this is the control.
 *
 * It wears the header control's box but keeps its filled treatment: every other
 * glyph up there acts on the page, and this one leaves it.
 *
 * `pointerdown` in the capture phase covers mouse, touch and pen with one
 * listener, and gets ahead of Radix — see {@link pressLandsOnButton}.
 */
export const MarkerIoReport = () => {
  const { markerSdk } = useMarkerSdk();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!markerSdk) return;

    const handleEvent = (e: PointerEvent) => {
      const button = buttonRef.current;
      if (!button || !pressLandsOnButton(button, e)) return;

      // Stop all event propagation to prevent Radix from closing dropdowns
      e.stopImmediatePropagation();
      e.preventDefault();
      openReporter(markerSdk);
    };

    window.addEventListener("pointerdown", handleEvent, true);
    return () => {
      window.removeEventListener("pointerdown", handleEvent, true);
    };
  }, [markerSdk]);

  if (!markerSdk) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={500} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            aria-label="Report a bug"
            variant="blank"
            size="icon"
            className={cn(panelHeaderControlHeightClass, "w-10 shrink-0 md:w-7")}
            // The pointer route in is the capture listener above, which a
            // keyboard never reaches — and this button now sits in a header
            // group where every neighbour answers the keyboard, so landing on
            // it and getting silence reads as broken. Preventing the default
            // also stops the browser synthesising a click from the key, so
            // there is no second path to report twice through.
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              openReporter(markerSdk);
            }}
            data-statsig-name="feedback-bug-report"
          >
            {/* The box matches its neighbours so the row stays level and the hit
                target stays thumb-sized, but the mark inside it does not fill
                that box. Every other glyph up here is a 16px outline; a disc at
                the same 28px carries several times the ink and reads as a
                bigger control rather than a peer.

                So the disc takes the mark size every neighbour uses — 20px on a
                phone, 16px from `md`. That leaves it the same empty box around
                it that they carry, which is what holds the row's spacing even. */}
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80 md:size-4">
              <Bug className="size-3 md:size-2.5" />
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Report a bug or issue</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
